import { useState } from "react";
import { Dialog, Box, Typography, InputBase } from "@mui/material";
import ClosegreyBGSvg from "@/assets/svg/ClosegreyBGSvg";
import SearchSvg from "@/assets/svg/SearchSvg";
import { styles } from "./stylesMarketSelection";
import SelectedMarket from "./components/SelectedMarket";
import SmallBlueSwitch from "./components/SmallBlueSwitch";
import { mockMarkets } from "./components/mockMarkets";
import MarketItem from "./components/MarketItem";

interface MarketSelectionProps {
  open: boolean;
  onClose: () => void;
}

const MarketSelectionModal: React.FC<MarketSelectionProps> = ({ open, onClose }) => {
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const toggleMarket = (name: string) => {
    setSelectedMarkets((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const removeMarket = (name: string) => {
    setSelectedMarkets((prev) => prev.filter((n) => n !== name));
  };

  const handleToggleAll = () => {
    if (selectAll) {
      setSelectedMarkets([]);
    } else {
      setSelectedMarkets(mockMarkets.map((m) => m.name));
    }
    setSelectAll(!selectAll);
  };

  const filteredMarkets = mockMarkets.filter((market) =>
    market.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: styles.modal }}
      className="hide-scrollbar"
    >
      <Box sx={styles.modalHeader}>
        <Typography sx={styles.modalTitle}>Markets</Typography>
        <Box onClick={onClose}>
          <ClosegreyBGSvg />
        </Box>
      </Box>

      <Box sx={styles.selectedBox}>
        <Typography sx={styles.selectedLabel}>
          Selected ({selectedMarkets.length != mockMarkets.length ? selectedMarkets.length : "All"})
        </Typography>
        <Box sx={styles.allMarketsToggle}>
          <Typography sx={styles.toggleText}>All Markets</Typography>
          <SmallBlueSwitch checked={selectAll} onChange={handleToggleAll} />
        </Box>
      </Box>

      {selectedMarkets.length != mockMarkets.length && selectedMarkets.length != 0 && (
        <Box sx={styles.selectedBlock}>
          {selectedMarkets.map((name) => {
            const market = mockMarkets.find((m) => m.name === name);
            if (!market) return null;
            return (
              <SelectedMarket
                key={name}
                name={name}
                Icon={() => market.icon}
                onRemove={() => removeMarket(name)}
              />
            );
          })}
        </Box>
      )}

      <Box sx={styles.searchWrapper}>
        <SearchSvg />
        <InputBase
          placeholder="Search Markets"
          sx={styles.searchInput}
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Box>
      {searchQuery && (
        <Box sx={styles.searchResultsRow}>
          <Typography sx={styles.searchResults}>Results</Typography>
          <Typography sx={styles.searchResultsCount}>({filteredMarkets.length})</Typography>
        </Box>
      )}
      <Box sx={styles.marketListWrapper}>
        <Box sx={styles.marketList}>
          {filteredMarkets.length != 0 ? (
            filteredMarkets.map((market) => (
              <MarketItem
                key={market.id}
                name={market.name}
                exchanges={market.exchanges}
                icon={market.icon}
                checked={selectedMarkets.includes(market.name)}
                onToggle={() => toggleMarket(market.name)}
              />
            ))
          ) : (
            <Typography sx={styles.noResults}>
              Sorry, but we couldn't find anything matching with “{searchQuery}”.
            </Typography>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default MarketSelectionModal;
